import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect, useParams } from "react-router-dom";

import Button from "veneer/Button";
import TextInput from "veneer/TextInput";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  REGISTER_USER,
  USER_INVITE_DETAILS,
} from "cortex/ui/src/redux-store/users/actions";
import { PasswordInput } from "cortex/ui/src/ui/form";
import Notification from "cortex/ui/src/ui/notification";
import { ROLE, RegisterUser } from "cortex/ui/src/user/model";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";

import BaseLoginView from "./BaseLoginView";

type RegisterUserWithPassword = Pick<
  RegisterUser,
  "account_id" | "account_name" | "email" | "name" | "role"
> & {
  p1: string;
  p2: string;
};

const defaultFormValues: RegisterUserWithPassword = {
  account_id: "",
  account_name: "",
  email: "",
  name: "",
  p1: "",
  p2: "",
  role: ROLE.READ_ONLY,
};

export default function Register() {
  const dispatch = useAppDispatch();
  const { id: token } = useParams<{ id: string }>();

  const [form, setForm] = useState<RegisterUserWithPassword>(defaultFormValues);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [passwordMeta, setPasswordMeta] = useState({ valid: false, score: 0 });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getUserInviteDetails = useCallback(
    (inviteToken: string) =>
      createPromiseApiAction(dispatch, USER_INVITE_DETAILS, [inviteToken], ""),
    [dispatch],
  );

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    getUserInviteDetails(token)
      .then((resp) => {
        if (cancelled) return;
        const response = (
          resp as { data?: { data?: Partial<RegisterUserWithPassword> } }
        )?.data?.data;
        setForm((prev) => ({
          ...prev,
          ...response,
          p1: prev.p1 ?? "",
          p2: prev.p2 ?? "",
        }));
        setErrorMessage("");
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage("Invalid registration link.");
      });

    return () => {
      cancelled = true;
    };
  }, [getUserInviteDetails, token]);

  const accountAccess = useMemo(() => {
    if (!form.account_name) return "";
    const roleLabel =
      form.role === ROLE.READ_ONLY ? "Read only" : "Full access";
    return `${form.account_name} / ${roleLabel}`;
  }, [form.account_name, form.role]);

  const onRegister = useCallback(
    (inviteId: string, name: string, p1: string, p2: string) =>
      createPromiseApiAction(
        dispatch,
        REGISTER_USER,
        [inviteId, name, p1, p2],
        "",
      ),
    [dispatch],
  );

  const doRegister = useCallback(async () => {
    if (!token) return;

    try {
      await onRegister(token, form.name, form.p1, form.p2);
      setErrorMessage("");
      setSuccessMessage("Your account was created successfully.");
    } catch (resp: unknown) {
      setErrorMessage(
        getErrorMessage(resp as Error | ResponseLike | null) ||
          "Failed to register.",
      );
      setSuccessMessage("");
    } finally {
      setSubmitted(false);
    }
  }, [form.name, form.p1, form.p2, onRegister, token]);

  const submit = useCallback(() => {
    if (submitted) return;
    setSubmitted(true);
    void doRegister();
  }, [doRegister, submitted]);

  if (!token) {
    return <Redirect to="/login" />;
  }

  return (
    <BaseLoginView width={500}>
      <Notification
        error={errorMessage}
        success={successMessage}
        successChildren={<Link to="/login">Login</Link>}
      />
      <TextInput
        disabled
        fullWidth
        label="Email"
        onChange={() => {}}
        size="medium"
        value={form.email ?? ""}
      />
      <TextInput
        disabled
        fullWidth
        label="Account / Access"
        onChange={() => {}}
        size="medium"
        value={accountAccess}
      />
      <TextInput
        fullWidth
        label="Name"
        onChange={(val) => setForm({ ...form, name: val ?? "" })}
        size="medium"
        value={form.name ?? ""}
      />
      <PasswordInput
        minLength={8}
        onChange={(next, meta) => {
          setForm((prev) => ({
            ...prev,
            p1: next.password,
            p2: next.retype ?? "",
          }));
          setPasswordMeta(meta);
        }}
        value={{ password: form.p1 ?? "", retype: form.p2 ?? "" }}
        withRetype
      />
      <Button
        disabled={submitted || !passwordMeta.valid}
        fullWidth
        loading={submitted}
        onClick={submit}
        size="large"
      >
        Save
      </Button>
    </BaseLoginView>
  );
}
